import { useInView } from "@/hooks/useInView";
import { Link } from "react-router-dom";
import LeadForm from "@/components/LeadForm";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { Mail, Phone, MapPin } from "lucide-react";

const WHATSAPP_URL =
  "https://wa.me/48512929641?text=" +
  encodeURIComponent("Dzień dobry, mam pytanie o dzierżawę nieruchomości pod maszt BTS.");

export default function FooterCTA() {
  const [ref, inView] = useInView();

  return (
    <>
      <section id="kontakt" className="py-24 bg-emerald-700" data-testid="footer-cta-section">
        <div className="max-w-7xl mx-auto px-6">
          <div
            ref={ref}
            className={`grid lg:grid-cols-2 gap-14 items-start transition-all duration-700 ${
              inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <div className="lg:pt-2">
              <h2
                className="text-4xl sm:text-5xl font-black text-white tracking-tight leading-[1.05] mb-5"
                data-testid="footer-cta-headline"
              >
                Gotowy, żeby Twoja ziemia zaczęła pracować?
              </h2>
              <p className="text-emerald-50 text-lg leading-relaxed mb-8">
                Zostaw kontakt — nasz specjalista odezwie się w ciągu 24 godzin,
                by omówić potencjał Twojej nieruchomości. Bez zobowiązań, bez
                kosztów.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  "Odpowiedź w ciągu 24 godzin",
                  "Bezpłatna wycena i konsultacja",
                  "Żadnych zobowiązań na tym etapie",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3 text-emerald-50 text-sm font-medium">
                    <div className="w-2 h-2 bg-emerald-300 rounded-full flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>

              <div className="pt-6 border-t border-white/20">
                <p className="text-emerald-100 text-sm font-medium mb-3">
                  Wolisz porozmawiać? Zadzwoń lub napisz — oddzwonimy.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="tel:+48512929641"
                    className="flex items-center justify-center gap-2 bg-white text-emerald-700 font-bold px-5 py-3 rounded-full hover:bg-emerald-50 transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-emerald-700"
                  >
                    <Phone className="w-4 h-4" aria-hidden="true" />
                    <span className="tabular-nums">512&nbsp;929&nbsp;641</span>
                  </a>
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-emerald-600 text-white font-bold px-5 py-3 rounded-full ring-1 ring-white/30 hover:bg-emerald-500 transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-emerald-700"
                  >
                    <WhatsAppIcon className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-2xl shadow-emerald-900/20">
              <h3 className="text-xl font-bold text-slate-900 mb-1">
                Wyślij zgłoszenie
              </h3>
              <p className="text-slate-600 text-sm mb-6">
                Twoje dane są bezpieczne i nie będą udostępniane.
              </p>
              <LeadForm />
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-slate-900 pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="mb-4">
                <img
                  src="/logo/logo-wordmark-light.svg"
                  alt="Telecom BTS"
                  width="240"
                  height="48"
                  className="h-8 w-auto"
                />
              </div>
              <p className="text-slate-400 text-sm leading-relaxed mb-3">
                Dzierżawa gruntów i dachów pod maszty 5G w całej Polsce.
              </p>
              <p className="text-slate-500 text-xs leading-relaxed">
                Polska spółka, decyzje podejmowane w Polsce, umowa z polskim podmiotem.
                Pełna obsługa formalna i prawna po naszej stronie.
              </p>
            </div>

            <div>
              <h3 className="text-white font-semibold text-sm mb-4">Strona</h3>
              <ul className="space-y-2.5 text-sm">
                <li>
                  <a href="#dla-kogo" className="text-slate-400 hover:text-emerald-400 transition-colors">
                    Dla kogo
                  </a>
                </li>
                <li>
                  <a href="#jak-to-dziala" className="text-slate-400 hover:text-emerald-400 transition-colors">
                    Jak to działa
                  </a>
                </li>
                <li>
                  <a href="#realizacje" className="text-slate-400 hover:text-emerald-400 transition-colors">
                    Realizacje
                  </a>
                </li>
                <li>
                  <a href="#faq" className="text-slate-400 hover:text-emerald-400 transition-colors">
                    FAQ
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold text-sm mb-4">Kontakt</h3>
              <ul className="space-y-2.5 text-sm">
                <li className="flex items-start gap-2 text-slate-400">
                  <Phone className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" aria-hidden="true" />
                  <a href="tel:+48512929641" className="hover:text-emerald-400 transition-colors">
                    +48 512 929 641
                  </a>
                </li>
                <li className="flex items-start gap-2 text-slate-400">
                  <span className="mt-0.5 flex-shrink-0 text-[#25D366]">
                    <WhatsAppIcon className="w-4 h-4" />
                  </span>
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-emerald-400 transition-colors"
                  >
                    WhatsApp
                  </a>
                </li>
                <li className="flex items-start gap-2 text-slate-400">
                  <Mail className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" aria-hidden="true" />
                  <a href="mailto:biuro@telecombts.pl" className="hover:text-emerald-400 transition-colors">
                    biuro@telecombts.pl
                  </a>
                </li>
                <li className="flex items-start gap-2 text-slate-400">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" aria-hidden="true" />
                  <span>Cała Polska</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold text-sm mb-4">Informacje prawne</h3>
              <ul className="space-y-2.5 text-sm">
                <li>
                  <Link to="/polityka-prywatnosci" className="text-slate-400 hover:text-emerald-400 transition-colors">
                    Polityka prywatności
                  </Link>
                </li>
                <li>
                  <Link to="/regulamin" className="text-slate-400 hover:text-emerald-400 transition-colors">
                    Regulamin
                  </Link>
                </li>
                <li>
                  <Link to="/rodo" className="text-slate-400 hover:text-emerald-400 transition-colors">
                    RODO
                  </Link>
                </li>
                <li>
                  <Link to="/cookies" className="text-slate-400 hover:text-emerald-400 transition-colors">
                    Polityka cookies
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-slate-500 text-xs">
              © 2026 Telecom BTS. Wszelkie prawa zastrzeżone.
            </p>
            <p className="text-slate-600 text-xs">
              Telecom BTS sp. z o.o. · ul. Adama Mickiewicza 55, 01-625 Warszawa · NIP 525-28-44-929 · KRS 0000872371 · REGON 387672774 · kapitał zakładowy 50 000 zł
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}
